import {
  MAXIMUM_SAVED_VIEW_NAME_LENGTH,
  type CreateSavedViewRequest,
  type ReportFilterStateV1,
  type SavedView,
  type UpdateSavedViewRequest,
} from "@meta-video/contracts";
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  Popover,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useEffect, useRef, useState } from "react";

import { returnFocus } from "../../app/a11y.js";
import { SessionExpiredError } from "../../shared/api/client.js";
import { useProductAnalytics } from "../../shared/analytics/useProductAnalytics.js";
import {
  createSavedView,
  deleteSavedView,
  SavedViewApiError,
  updateSavedView,
} from "./api.js";

export interface SavedViewsWriteApi {
  readonly createSavedView: (input: CreateSavedViewRequest) => Promise<SavedView>;
  readonly deleteSavedView: (viewId: string) => Promise<void>;
  readonly updateSavedView: (
    viewId: string,
    input: UpdateSavedViewRequest,
  ) => Promise<SavedView>;
}

const defaultApi: SavedViewsWriteApi = {
  createSavedView,
  deleteSavedView,
  updateSavedView,
};

export interface SavedViewHeaderActionsProps {
  readonly activeView: SavedView | null;
  readonly api?: SavedViewsWriteApi;
  readonly filters: ReportFilterStateV1;
  readonly isDirty: boolean;
  readonly isPartial: boolean;
  readonly onChanged: (
    view: SavedView,
    expectedWorkspaceKey: string,
  ) => void | Promise<void>;
  readonly onMetadataChanged: (view?: SavedView) => void | Promise<void>;
  readonly onSessionExpired?: () => void;
  readonly workspaceKey: string | null;
  readonly createLabel?: string;
  readonly compactLabel?: string;
}

export function SavedViewHeaderActions({
  activeView,
  api = defaultApi,
  filters,
  isDirty,
  isPartial,
  onChanged,
  onMetadataChanged,
  onSessionExpired,
  workspaceKey,
  createLabel = "Save",
  compactLabel,
}: SavedViewHeaderActionsProps) {
  const analytics = useProductAnalytics();
  const [conflict, setConflict] = useState<Readonly<{
    accessRole: SavedView["accessRole"];
    current: SavedView;
    filters: ReportFilterStateV1;
    filtersKey: string;
    workspaceKey: string;
    viewId: string;
  }> | null>(null);
  const [partialContext, setPartialContext] = useState<Readonly<{
    accessRole: SavedView["accessRole"];
    filtersKey: string;
    revision: number;
    viewId: string;
    workspaceKey: string;
  }> | null>(null);
  const [confirmPartial, setConfirmPartial] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const [name, setName] = useState("");
  const [pending, setPending] = useState(false);
  const actionTriggerRef = useRef<HTMLButtonElement>(null);
  const activeViewId = activeView?.id ?? null;
  const headerContext = {
    accessRole: activeView?.accessRole ?? null,
    filtersKey: JSON.stringify(filters),
    revision: activeView?.revision ?? null,
    viewId: activeViewId,
    workspaceKey,
  };
  const headerContextRef = useRef(headerContext);
  headerContextRef.current = headerContext;
  const canUpdate = activeView?.accessRole === "OWNER" || activeView?.accessRole === "EDIT";
  const label = activeView && canUpdate
    ? isDirty ? "Update Saved View" : "Saved"
    : createLabel;
  const compactActionLabel = label === "Update Saved View"
    ? "Update"
    : label === "Saved"
      ? "Saved"
      : createLabel;

  useEffect(() => {
    setConflict(null);
    setConfirmPartial(false);
    setPartialContext(null);
    setCreating(false);
    setError(null);
    setIsDefault(false);
    setName("");
  }, [activeViewId]);

  useEffect(() => {
    if (canUpdate) return;
    setConflict(null);
    setConfirmPartial(false);
    setPartialContext(null);
  }, [canUpdate]);

  useEffect(() => {
    if (
      !confirmPartial
      || partialContext === null
      || (
        headerContext.filtersKey === partialContext.filtersKey
        && headerContext.accessRole === partialContext.accessRole
        && headerContext.revision === partialContext.revision
        && headerContext.viewId === partialContext.viewId
        && headerContext.workspaceKey === partialContext.workspaceKey
      )
    ) return;
    setConfirmPartial(false);
    setPartialContext(null);
  }, [
    confirmPartial,
    headerContext.accessRole,
    headerContext.filtersKey,
    headerContext.revision,
    headerContext.viewId,
    headerContext.workspaceKey,
    partialContext,
  ]);

  useEffect(() => {
    if (
      conflict !== null
      && (
        headerContext.filtersKey !== conflict.filtersKey
        || headerContext.accessRole !== conflict.accessRole
        || headerContext.viewId !== conflict.viewId
        || headerContext.workspaceKey !== conflict.workspaceKey
      )
    ) {
      setConflict(null);
      void onMetadataChanged();
    }
  }, [
    conflict,
    headerContext.accessRole,
    headerContext.filtersKey,
    headerContext.viewId,
    headerContext.workspaceKey,
    onMetadataChanged,
  ]);

  const run = async (
    request: () => Promise<void>,
    conflictContext?: Readonly<{
      accessRole: SavedView["accessRole"];
      filters: ReportFilterStateV1;
      filtersKey: string;
      revision: number;
      viewId: string;
      workspaceKey: string;
    }>,
  ) => {
    setError(null);
    setPending(true);
    try {
      await request();
    } catch (caught) {
      if (caught instanceof SessionExpiredError) {
        onSessionExpired?.();
        return;
      }
      if (caught instanceof SavedViewApiError && caught.code === "SAVED_VIEW_REVISION_CONFLICT" && caught.current) {
        if (
          conflictContext
          && headerContextRef.current.accessRole === conflictContext.accessRole
          && headerContextRef.current.viewId === conflictContext.viewId
          && headerContextRef.current.revision === conflictContext.revision
          && headerContextRef.current.filtersKey === conflictContext.filtersKey
          && headerContextRef.current.workspaceKey === conflictContext.workspaceKey
          && caught.current.id === conflictContext.viewId
        ) {
          setConfirmPartial(false);
          setPartialContext(null);
          setConflict({
            accessRole: conflictContext.accessRole,
            current: caught.current,
            filters: conflictContext.filters,
            filtersKey: conflictContext.filtersKey,
            viewId: conflictContext.viewId,
            workspaceKey: conflictContext.workspaceKey,
          });
        } else {
          await onMetadataChanged(caught.current);
        }
        return;
      }
      if (
        caught instanceof SavedViewApiError
        && (caught.code === "SAVED_VIEW_NOT_FOUND" || caught.code === "SAVED_VIEW_CONFLICT")
      ) await onMetadataChanged();
      setError("The Saved View could not be saved. Try again.");
    } finally {
      setPending(false);
    }
  };

  const update = (
    view: SavedView,
    submittedFilters: ReportFilterStateV1,
    expectedRevision: number,
  ) => {
    if (workspaceKey === null) return;
    const operationViewId = view.id;
    const submitted = {
      accessRole: view.accessRole,
      filters: submittedFilters,
      filtersKey: JSON.stringify(submittedFilters),
      revision: expectedRevision,
      viewId: operationViewId,
      workspaceKey,
    };
    void run(async () => {
      const changed = await api.updateSavedView(operationViewId, {
        expectedRevision,
        filters: submittedFilters,
      });
      setConflict(null);
      setConfirmPartial(false);
      if (
        headerContextRef.current.accessRole === submitted.accessRole
        && headerContextRef.current.filtersKey === submitted.filtersKey
        && headerContextRef.current.revision === submitted.revision
        && headerContextRef.current.viewId === submitted.viewId
        && headerContextRef.current.workspaceKey === submitted.workspaceKey
      ) await onChanged(changed, submitted.workspaceKey);
      else await onMetadataChanged(changed);
      analytics.savedViewAction("update");
    }, submitted);
  };

  return (
    <>
      <Popover
        onChange={(opened) => {
          if (!opened) setError(null);
        }}
        opened={error !== null && !creating && !confirmPartial && conflict === null}
        position="bottom-end"
        withArrow
      >
        <Popover.Target>
          <Button
            aria-label={label}
            disabled={workspaceKey === null || pending || Boolean(activeView && canUpdate && !isDirty)}
            loading={pending}
            onClick={() => {
              if (activeView && canUpdate) {
                if (
                  isPartial
                  && activeView
                  && workspaceKey !== null
                ) {
                  setPartialContext({
                    accessRole: activeView.accessRole,
                    filtersKey: headerContext.filtersKey,
                    revision: activeView.revision,
                    viewId: activeView.id,
                    workspaceKey,
                  });
                  setConfirmPartial(true);
                }
                else update(activeView, filters, activeView.revision);
              } else {
                setCreating(true);
              }
            }}
            ref={actionTriggerRef}
            size="xs"
            variant="default"
          >
            <span className={compactLabel ? "responsive-action-label__full" : undefined}>{label}</span>
            {compactLabel ? (
              <span aria-hidden="true" className="responsive-action-label__compact">
                {compactActionLabel}
              </span>
            ) : null}
          </Button>
        </Popover.Target>
        <Popover.Dropdown>
          {error ? <Alert color="red" role="alert">{error}</Alert> : null}
        </Popover.Dropdown>
      </Popover>

      <Modal onClose={() => setCreating(false)} opened={creating} title="Save current report">
        <form onSubmit={(event) => {
          event.preventDefault();
          const submittedName = name.trim();
          const submittedWorkspaceKey = workspaceKey;
          const submittedFilters = filters;
          if (submittedName === "" || submittedWorkspaceKey === null) return;
          void run(async () => {
            const created = await api.createSavedView({
              filters: submittedFilters,
              isDefault,
              name: submittedName,
            });
            setCreating(false);
            setIsDefault(false);
            setName("");
            await onChanged(created, submittedWorkspaceKey);
            analytics.savedViewAction("create");
          });
        }}>
          <Stack gap="sm">
            {error ? <Alert color="red" role="alert">{error}</Alert> : null}
            <TextInput
              autoFocus
              disabled={pending}
              label="View name"
              maxLength={MAXIMUM_SAVED_VIEW_NAME_LENGTH}
              onChange={(event) => setName(event.currentTarget.value)}
              value={name}
            />
            <Checkbox
              checked={isDefault}
              disabled={pending}
              label="Make this my default view"
              onChange={(event) => setIsDefault(event.currentTarget.checked)}
            />
            <Group justify="flex-end">
              <Button disabled={pending} onClick={() => setCreating(false)} type="button" variant="subtle">Cancel</Button>
              <Button disabled={name.trim() === "" || pending} loading={pending} type="submit">Create Saved View</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        onClose={() => setConfirmPartial(false)}
        opened={confirmPartial}
        returnFocus={conflict === null}
        title="Confirm reduced-scope update"
      >
        <Stack gap="sm">
          {error ? <Alert color="red" role="alert">{error}</Alert> : null}
          <Text>This Saved View is only partially available. Confirm that you want to overwrite it with your reduced permitted scope.</Text>
          <Group justify="flex-end">
            <Button disabled={pending} onClick={() => setConfirmPartial(false)} variant="subtle">Keep Saved View unchanged</Button>
            <Button
              color="orange"
              disabled={pending}
              onClick={() => {
                if (
                  activeView
                  && partialContext
                  && activeView.id === partialContext.viewId
                ) update(activeView, filters, partialContext.revision);
              }}
            >Confirm partial overwrite</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        onClose={() => setConflict(null)}
        onExitTransitionEnd={() => returnFocus(actionTriggerRef.current)}
        opened={conflict !== null}
        returnFocus={false}
        title="Saved View changed"
      >
        {conflict ? (
          <Stack gap="sm">
            {error ? <Alert color="red" role="alert">{error}</Alert> : null}
            <Alert color="orange" role="alert">
              <Text fw={600}>{conflict.current.name}</Text>
              <Text size="sm">Revision {conflict.current.revision} is current. Your filters were not applied.</Text>
            </Alert>
            <Group justify="flex-end">
              <Button
                data-autofocus
                disabled={pending}
                onClick={() => {
                  const current = conflict.current;
                  setConflict(null);
                  if (
                    headerContextRef.current.accessRole === conflict.accessRole
                    && headerContextRef.current.filtersKey === conflict.filtersKey
                    && headerContextRef.current.viewId === conflict.viewId
                    && headerContextRef.current.workspaceKey === conflict.workspaceKey
                  ) {
                    void onChanged(current, conflict.workspaceKey);
                  } else {
                    void onMetadataChanged(current);
                  }
                }}
                variant="subtle"
              >Use current saved state</Button>
              <Button
                disabled={pending
                  || !canUpdate
                  || headerContext.accessRole !== conflict.accessRole
                  || activeViewId !== conflict.viewId
                  || workspaceKey !== conflict.workspaceKey}
                loading={pending}
                onClick={() => {
                  if (workspaceKey !== conflict.workspaceKey) {
                    setConflict(null);
                    void onMetadataChanged();
                    return;
                  }
                  update(conflict.current, conflict.filters, conflict.current.revision);
                }}
              >Reapply my filters</Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>
    </>
  );
}
